import { create } from 'zustand';
import { processWithPreset, type BatchStep } from '@/batch/process-with-preset';
import { CONVERSION_SETTINGS } from '@/core/project/convert';
import { kindOf, type OutputFormatId } from '@/core/project/output-format';
import { BUILT_IN_PRESETS, type Preset } from '@/core/project/preset';
import { isCancelled, type Task } from '@/engine/client';
import type { EngineError } from '@/engine/errors';
import { findPresetById } from './preset-store';

export type BatchStatus = 'queued' | 'running' | 'done' | 'error' | 'cancelled';

export interface BatchItem {
  readonly id: string;
  readonly file: File;
  readonly status: BatchStatus;
  readonly step: BatchStep | null;
  readonly progress: number;
  readonly result: { readonly file: File; readonly url: string } | null;
  readonly error: EngineError | null;
}

interface BatchStore {
  readonly items: readonly BatchItem[];
  readonly presetId: string;
  /** Quick convert: every video saved as it is in this format; null runs the chosen recipe (as MP4). */
  readonly output: OutputFormatId | null;
  /** "Keep each under N MB" (videos only), or null. */
  readonly sizeLimit: number | null;
  readonly running: boolean;
  add(files: readonly File[]): void;
  /** Queues videos to be converted to `output` (quick convert with several files), each under `sizeLimit` MB if given. */
  convert(files: readonly File[], output: OutputFormatId, sizeLimit?: number | null): void;
  setSizeLimit(sizeLimit: number | null): void;
  /** Switches between converting (a format) and running a recipe (null); not while running. */
  setOutput(output: OutputFormatId | null): void;
  remove(id: string): void;
  clear(): void;
  setPreset(id: string): void;
  start(): Promise<void>;
  stop(): void;
}

let current: Task<unknown> | null = null;
let stopRequested = false;
let counter = 0;

export function findPreset(id: string): Preset {
  return findPresetById(id) ?? BUILT_IN_PRESETS[0]!;
}

/** Queue of videos processed one after another with a single preset. */
export const useBatchStore = create<BatchStore>()((set, get) => {
  const update = (id: string, patch: Partial<BatchItem>) =>
    set((state) => ({ items: state.items.map((item) => (item.id === id ? { ...item, ...patch } : item)) }));

  return {
    items: [],
    presetId: BUILT_IN_PRESETS[0]!.id,
    output: null,
    sizeLimit: null,
    running: false,

    add(files) {
      const fresh = files.map<BatchItem>((file) => ({ id: `batch-${++counter}`, file, status: 'queued', step: null, progress: 0, result: null, error: null }));
      set((state) => ({ items: [...state.items, ...fresh] }));
    },

    convert(files, output, sizeLimit = null) {
      if (get().running) return;
      set({ output, sizeLimit });
      get().add(files);
    },

    setSizeLimit(sizeLimit) {
      if (!get().running) set({ sizeLimit });
    },

    setOutput(output) {
      // A size limit only applies to videos.
      if (!get().running) set((state) => ({ output, sizeLimit: output && kindOf(output) === 'video' ? state.sizeLimit : null }));
    },

    remove(id) {
      const item = get().items.find((candidate) => candidate.id === id);
      if (!item || item.status === 'running') return;
      if (item.result) URL.revokeObjectURL(item.result.url);
      set((state) => ({ items: state.items.filter((candidate) => candidate.id !== id) }));
    },

    clear() {
      get().stop();
      for (const item of get().items) if (item.result) URL.revokeObjectURL(item.result.url);
      set({ items: [], running: false, output: null, sizeLimit: null });
    },

    setPreset(presetId) {
      if (!get().running) set({ presetId });
    },

    async start() {
      if (get().running) return;
      stopRequested = false;
      set((state) => ({
        running: true,
        // Re-run anything that didn't finish last time.
        items: state.items.map((item) => (item.status === 'done' ? item : { ...item, status: 'queued', progress: 0, step: null, error: null })),
      }));
      // Fixed for the whole run, like the recipe: switching is locked while it runs.
      const { output, sizeLimit } = get();
      const settings = output ? CONVERSION_SETTINGS : findPreset(get().presetId).settings;
      for (;;) {
        const next = get().items.find((item) => item.status === 'queued');
        if (!next || stopRequested) break;
        update(next.id, { status: 'running', progress: 0 });
        try {
          const result = await processWithPreset(
            next.file,
            settings,
            ({ step, ratio }) => update(next.id, { step, progress: ratio }),
            (task) => (current = task),
            { output: output ?? undefined, sizeLimit },
          );
          update(next.id, { status: 'done', progress: 1, step: null, result: { file: result.file, url: URL.createObjectURL(result.file) } });
        } catch (error) {
          update(next.id, isCancelled(error) ? { status: 'cancelled', step: null } : { status: 'error', step: null, error: error as EngineError });
        } finally {
          current = null;
        }
      }
      set({ running: false });
    },

    stop() {
      stopRequested = true;
      current?.cancel();
    },
  };
});
